import { useEffect, useMemo, useRef, useState } from "react";
import clsx from "clsx";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMagnifyingGlass, faRefresh } from "@awesome.me/kit-70c1f0055f/icons/classic/regular";
import { faExclamationTriangle } from "@awesome.me/kit-70c1f0055f/icons/classic/solid";
import { Button } from "@/components/ui/button/Button";
import FormInput from "@/components/ui/form-input";
import LoadingPlaceholder from "@/components/ui/loading-placeholder/LoadingPlaceholder";
import { SegmentedTabs } from "@/components/ui/segmented-tabs";
import { SegmentedPillToggle } from "@/components/ui/segmented-tabs/SegmentedPillToggle";
import {
  useOpenInNewWindowMenu,
  useOpenUrl,
  useOsNavigate,
  useSetWindowTitle,
} from "@/components/ui/os-layout";
import {
  WORKBENCH_ROOT_TEXT_CLASS,
  WORKBENCH_SEARCH_PILL_CLASS,
  WORKBENCH_SURFACE_CLASS,
} from "@/components/ui/workbench";
import type { FaClientListItem } from "@/services/api/agentic-v2";
import { VisibilityFilter } from "@/components/agentic-v2/VisibilityFilter";
import { useVirtualScrollElement } from "@/components/mission-control/useVirtualScrollElement";
import {
  CLIENTS_FETCH_LIMIT,
  DEFAULT_CLIENTS_FILTERS,
  useClientsPage,
  type ClientsFilters,
} from "@/services/api/agentic-v2/queries/useClientsPage";
import { useClientsStageSummary } from "@/services/api/agentic-v2/queries/useClientsStageSummary";
import type { ClientStageGroup, ClientsOnboardingScope } from "@/services/api/agentic-v2/types";
import { useCompanyId } from "@/context/CompanyContext";
import { isOpenworkCompany } from "@/components/ui/os-layout/openworkDockApps";
import { ClientsVirtualList } from "./ClientsVirtualList";
import { OpenworkClientsView } from "./OpenworkClientsView";
import {
  resolveStageFilter,
  type ClientsAppProfile,
} from "./clientsAppProfiles";
import type { ClientDetailTabId } from "./clientDetailTabs";
import { buildClientDetailWindowUrl } from "@/navigation/apps/clientDetailLocation";
import { useAgentListFocus } from "@/hooks/useAgentListFocus";

/**
 * Top-level Clients list view.
 *
 *  - Search + visibility filter at the top
 *  - Bucket tabs (Action needed / Stalled / Healthy / All) mirroring
 *    Mission Control's pattern so the advisor lands directly on the
 *    queue that needs them.
 *  - Bucket counts and the "N+" overflow indicator are computed from a
 *    single server fetch capped at {@link CLIENTS_FETCH_LIMIT}.
 */
type BucketId = "action" | "healthy" | "all";

const BUCKETS: BucketId[] = ["all", "action", "healthy"];

const BUCKET_LABELS: Record<BucketId, string> = {
  action: "Action needed",
  healthy: "Healthy",
  all: "All",
};

const ONBOARDING_SCOPE_OPTIONS: { id: ClientsOnboardingScope; label: string }[] = [
  { id: "onboarding", label: "Onboarding" },
  { id: "all", label: "All in stage" },
];

export interface ClientsListProps {
  /** When nested inside the paraplanner workbench — skip outer page chrome. */
  embedded?: boolean;
  profile?: ClientsAppProfile;
}

const DEFAULT_LIST_PROFILE: ClientsAppProfile = {
  id: "default",
  defaultLens: "all",
  lenses: ["all"],
  defaultVisibility: "all",
  showInsightBuckets: true,
};

export function ClientsList({ embedded = false, profile }: ClientsListProps = {}) {
  const setWindowTitle = useSetWindowTitle();
  const titleSetRef = useRef(false);
  useEffect(() => {
    if (embedded || titleSetRef.current) return;
    setWindowTitle("Clients");
    titleSetRef.current = true;
  }, [setWindowTitle, embedded]);

  const osNavigate = useOsNavigate();
  const openUrl = useOpenUrl();
  const newWindowMenu = useOpenInNewWindowMenu();
  const {
    ref: scrollRef,
    element: scrollElement,
    scrollReady,
  } = useVirtualScrollElement<HTMLDivElement>();

  const companyId = useCompanyId();
  const isOpenwork = isOpenworkCompany(companyId);
  const resolvedProfile = profile ?? DEFAULT_LIST_PROFILE;
  const showInsightBuckets = resolvedProfile.showInsightBuckets;
  const stageFilters = resolvedProfile.stageFilters;
  const showOnboardingScopeToggle = resolvedProfile.showOnboardingScopeToggle ?? false;
  const defaultOnboardingScope =
    resolvedProfile.defaultOnboardingScope ?? "all";
  const defaultStageFilterId =
    resolvedProfile.defaultStageFilter ?? stageFilters?.[0]?.id ?? "all";
  const [activeStageFilter, setActiveStageFilter] = useState(defaultStageFilterId);
  const activeStagePill = resolveStageFilter(resolvedProfile, activeStageFilter);

  const [filters, setFilters] = useState<ClientsFilters>(() => ({
    ...DEFAULT_CLIENTS_FILTERS,
    visibility: resolvedProfile.defaultVisibility,
    stageGroup: resolveStageFilter(resolvedProfile, defaultStageFilterId)?.stageGroup,
    onboardingScope: showOnboardingScopeToggle ? defaultOnboardingScope : "all",
  }));
  const [bucket, setBucket] = useState<BucketId>("all");
  const { clients, loading, refetching, error, hasMore, refresh } =
    useClientsPage({ filters });
  const stageSummaryQuery = useClientsStageSummary({
    visibility: filters.visibility,
    onboardingScope: filters.onboardingScope ?? "all",
    enabled: Boolean(stageFilters?.length),
  });
  const stageSummary = stageSummaryQuery.data;

  const bucketed = useMemo(() => bucketClients(clients), [clients]);
  const visibleClients = showInsightBuckets ? bucketed[bucket] : clients;
  useAgentListFocus({
    focusPrefix: "client",
    rows: visibleClients,
    getRowId: (row) => row._id,
    enabled: scrollReady,
  });
  const listKey = showInsightBuckets
    ? bucket
    : `${activeStageFilter}:${filters.onboardingScope ?? "all"}`;

  const refreshAll = () => {
    refresh();
    void stageSummaryQuery.refetch();
  };

  const onStageFilterChange = (id: string) => {
    setActiveStageFilter(id);
    const pill = stageFilters?.find((entry) => entry.id === id);
    setFilters((prev) => ({ ...prev, stageGroup: pill?.stageGroup }));
  };

  const onOnboardingScopeChange = (scope: ClientsOnboardingScope) => {
    setFilters((prev) => ({ ...prev, onboardingScope: scope }));
  };

  const stagePillCount = (pillId: string, stageGroup?: ClientStageGroup): number | undefined => {
    if (!stageSummary) return undefined;
    if (pillId === "all" || !stageGroup) return stageSummary.total;
    return stageSummary.groups[stageGroup];
  };

  const stagePillOptions = (stageFilters ?? []).map((pill) => {
    const count = stagePillCount(pill.id, pill.stageGroup);
    return { id: pill.id, label: count == null ? pill.label : `${pill.label} · ${count}` };
  });

  const listShowsTruncatedSample = hasMore && clients.length >= CLIENTS_FETCH_LIMIT;

  // Full-name search can match a client that isn't in the active bucket tab.
  useEffect(() => {
    const q = filters.q.trim();
    if (!q || bucket === "all") return;
    const split = bucketClients(clients);
    if (split[bucket].length === 0 && split.all.length > 0) {
      setBucket("all");
    }
  }, [filters.q, clients, bucket]);

  const clientDetailPath = (client: FaClientListItem) => {
    const openDataGathering =
      Boolean(client.in_data_gathering_phase) && filters.stageGroup === "in_dg";
    const initialTab: ClientDetailTabId | undefined = openDataGathering
      ? "plans"
      : undefined;
    return buildClientDetailWindowUrl(
      client._id,
      initialTab ? { tab: initialTab } : undefined,
    );
  };

  const navigateToClient = (client: FaClientListItem) => {
    const path = clientDetailPath(client);
    if (openUrl(path, { title: client.display_name })) return;
    osNavigate(path);
  };

  const listContent = (
    <>
        <div className="flex items-center gap-3">
          <div className="flex-1">
            <FormInput
              type="search"
              placeholder="Search clients..."
              value={filters.q}
              icon={<FontAwesomeIcon icon={faMagnifyingGlass} />}
              iconPosition="start"
              data-testid="clients-search-input"
              className={clsx(
                "w-full min-w-0 rounded-xl py-2.5 text-sm",
                WORKBENCH_SEARCH_PILL_CLASS,
              )}
              onChange={(e) =>
                setFilters({ ...filters, q: e.target.value })
              }
            />
          </div>
          <VisibilityFilter
            value={filters.visibility}
            onChange={(visibility) => setFilters({ ...filters, visibility })}
          />
          <Button
            plain
            onClick={refreshAll}
            loading={refetching}
            aria-label="Refresh clients"
            data-testid="clients-refresh"
          >
            <FontAwesomeIcon icon={faRefresh} />
          </Button>
        </div>

        {stageFilters && stageFilters.length > 0 ? (
          <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
            <div className="flex flex-wrap items-center gap-3 min-w-0">
              <SegmentedPillToggle
                options={stagePillOptions}
                value={activeStageFilter}
                onChange={onStageFilterChange}
                aria-label="Client journey stage"
                data-testid="clients-stage-pills"
                optionTestIdPrefix="clients-stage"
              />
              {refetching || stageSummaryQuery.isFetching ? (
                <span className="text-xs text-slate-500">Updating…</span>
              ) : null}
            </div>
            {showOnboardingScopeToggle ? (
              <SegmentedPillToggle
                options={ONBOARDING_SCOPE_OPTIONS}
                value={filters.onboardingScope ?? "onboarding"}
                onChange={onOnboardingScopeChange}
                aria-label="Caseload scope"
                data-testid="clients-onboarding-scope"
                optionTestIdPrefix="clients-onboarding-scope"
              />
            ) : null}
          </div>
        ) : null}

        {!isOpenwork && showInsightBuckets ? (
          <div className="mt-5 border-b border-slate-200 dark:border-white/5">
            <SegmentedTabs
              tabTestIdPrefix="clients-bucket"
              showDivider={false}
              tabs={BUCKETS.map((id) => ({
                id,
                label: BUCKET_LABELS[id],
                count: bucketed[id].length || undefined,
                countLabel: bucketCountLabel(id, bucketed[id].length, hasMore),
              }))}
              activeId={bucket}
              onChange={(id) => setBucket(id as BucketId)}
            />
          </div>
        ) : null}

        <div className="mt-4 flex-1 min-h-0 flex flex-col">
          {loading ? (
            <div className="space-y-2">
              <LoadingPlaceholder height="72px" />
              <LoadingPlaceholder height="72px" />
              <LoadingPlaceholder height="72px" />
            </div>
          ) : error ? (
            <ErrorState message={error.message} onRetry={refreshAll} />
          ) : visibleClients.length === 0 ? (
            <EmptyState
              bucket={bucket}
              hasQuery={filters.q.trim() !== ""}
              visibility={filters.visibility}
              stageLabel={activeStagePill?.label}
              hasStageFilter={Boolean(filters.stageGroup)}
              onboardingOnly={filters.onboardingScope === "onboarding"}
            />
          ) : isOpenwork ? (
            <div className="flex-1 min-h-0 overflow-y-auto">
              <OpenworkClientsView clients={visibleClients} />
            </div>
          ) : (
            <div
              ref={scrollRef}
              className="flex-1 min-h-0 overflow-y-auto -mx-2"
            >
              <ClientsVirtualList
                clients={visibleClients}
                refreshing={refetching}
                onClientClick={navigateToClient}
                onClientContextMenu={(event, client) =>
                  newWindowMenu.onContextMenu(
                    event,
                    clientDetailPath(client),
                    client.display_name,
                  )
                }
                scrollElement={scrollElement}
                scrollReady={scrollReady}
                listKey={listKey}
              />
              {newWindowMenu.menu}
              {listShowsTruncatedSample && (
                <p className="text-[11px] text-slate-500 text-center py-3">
                  Showing first {CLIENTS_FETCH_LIMIT} of{" "}
                  {filters.stageGroup
                    ? stagePillCount(activeStageFilter, activeStagePill?.stageGroup) ??
                      "many"
                    : stageSummary?.total ?? "many"}
                  . Refine search to narrow further.
                </p>
              )}
            </div>
          )}
        </div>
    </>
  );

  if (embedded) {
    return (
      <div className="flex h-full min-h-0 flex-col" data-testid="clients-list-embedded">
        {listContent}
      </div>
    );
  }

  return (
    <div
      className={clsx("flex flex-col h-full w-full p-4", WORKBENCH_ROOT_TEXT_CLASS)}
      data-testid="clients-list"
    >
      <div className={clsx("flex-1 min-h-0 flex flex-col rounded-2xl px-6 py-5", WORKBENCH_SURFACE_CLASS)}>
        {listContent}
      </div>
    </div>
  );
}

/**
 * Split clients across buckets. Each client appears in "all"; the
 * action/healthy buckets are derived from the AI insight stub.
 *
 *   action  — insight flagged actionable
 *   healthy — insight status green, no action needed
 *   all     — everything (including clients with no insight yet)
 */
function bucketClients(
  clients: FaClientListItem[],
): Record<BucketId, FaClientListItem[]> {
  const out: Record<BucketId, FaClientListItem[]> = {
    action: [],
    healthy: [],
    all: clients,
  };
  for (const c of clients) {
    const insight = c.ai_insight;
    if (!insight) continue;
    if (insight.actionable) {
      out.action.push(c);
    } else if (insight.status === "green") {
      out.healthy.push(c);
    }
  }
  return out;
}

/** "500+" only on the All tab when capped; other buckets show plain counts. */
function bucketCountLabel(
  id: BucketId,
  count: number,
  hasMore: boolean,
): string | undefined {
  if (count === 0) return undefined;
  if (id === "all" && hasMore) return `${CLIENTS_FETCH_LIMIT}+`;
  return String(count);
}

function EmptyState({
  bucket,
  hasQuery,
  visibility = "all",
  stageLabel,
  hasStageFilter = false,
  onboardingOnly = false,
}: {
  bucket: BucketId;
  hasQuery: boolean;
  visibility?: ClientsFilters["visibility"];
  stageLabel?: string;
  hasStageFilter?: boolean;
  onboardingOnly?: boolean;
}) {
  const message = hasQuery
    ? "No clients match your search."
    : hasStageFilter && stageLabel && onboardingOnly
      ? `No clients at stage “${stageLabel}” with an active onboarding mission.`
      : hasStageFilter && stageLabel
        ? `No clients at stage “${stageLabel}” on this caseload.`
        : visibility !== "all"
          ? "No clients on your caseload for this scope. Try Everyone, or check that your Plannr email matches the assigned paraplanner on the household."
          : EMPTY_BUCKET_COPY[bucket];
  return (
    <div className="flex-1 flex flex-col items-center justify-center text-slate-500 py-12 px-6 text-center">
      <p className="text-sm">{message}</p>
    </div>
  );
}

const EMPTY_BUCKET_COPY: Record<BucketId, string> = {
  action: "All clear — no clients need attention.",
  healthy: "No clients with a healthy signal yet.",
  all: "No clients yet.",
};

function ErrorState({
  message,
  onRetry,
}: {
  message: string;
  onRetry: () => void;
}) {
  return (
    <div className="flex flex-1 flex-col items-center justify-center gap-3 py-12 text-slate-600 dark:text-slate-300">
      <FontAwesomeIcon
        icon={faExclamationTriangle}
        className="text-amber-500 text-2xl"
      />
      <p className="text-sm">Failed to load clients: {message}</p>
      <Button onClick={onRetry}>Retry</Button>
    </div>
  );
}
